import { useMemo, useState } from 'react';
import { getAllResults, runBenchmarkProfile, runMultiRuntimeBenchmark } from '../bench.js';
import { BENCHMARK_PROFILES, MODELS, QUANT_LABEL, findModel } from '../catalog.js';
import { recommendQuantForMachine, recommendRuntimeForMachine } from '../fit.js';
import type { BenchmarkResult, MachineProfile, Quantization, RuntimeId, TaskCategory } from '../types.js';
import { Bar, VerdictPill } from '../ui.js';

interface BenchTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
}

const ALL_RUNTIMES: RuntimeId[] = ['ollama', 'lm-studio', 'llama-cpp', 'mlx', 'vllm'];

export function BenchTab({ machine, mode }: BenchTabProps) {
  const [modelId, setModelId] = useState<string>('qwen-2.5-coder-7b');
  const model = findModel(modelId) ?? MODELS[0];
  const defaultQuant = recommendQuantForMachine(machine, model);
  const defaultRuntime = recommendRuntimeForMachine(machine);
  const [quant, setQuant] = useState<Quantization>(defaultQuant);
  const [runtime, setRuntime] = useState<RuntimeId>(defaultRuntime);
  const [mode2, setMode2] = useState<'single' | 'multi-runtime'>('single');
  const [multiRuntimes, setMultiRuntimes] = useState<RuntimeId[]>([defaultRuntime]);
  const [profileId, setProfileId] = useState<string>(BENCHMARK_PROFILES[0].id);
  const [useLiveOllama, setUseLiveOllama] = useState(false);
  const [customPromptsOpen, setCustomPromptsOpen] = useState(false);
  const [customPrompts, setCustomPrompts] = useState<Partial<Record<TaskCategory, string>>>({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; latest?: BenchmarkResult }>({
    done: 0,
    total: 0,
  });
  const [runStats, setRunStats] = useState<BenchmarkResult[]>([]);

  const activeQuant = model.quantizations.includes(quant) ? quant : defaultQuant;
  const history = useMemo(() => getAllResults().filter((r) => r.machineId === machine.id), [machine.id]);

  async function handleStart() {
    const profile = BENCHMARK_PROFILES.find((p) => p.id === profileId) ?? BENCHMARK_PROFILES[0];
    setRunning(true);
    setRunStats([]);
    const runtimeCount = mode2 === 'multi-runtime' ? multiRuntimes.length : 1;
    setProgress({ done: 0, total: profile.tasks.length * profile.iterations * runtimeCount });
    const collected: BenchmarkResult[] = [];
    try {
      if (mode2 === 'multi-runtime' && multiRuntimes.length > 0) {
        await runMultiRuntimeBenchmark({
          machineId: machine.id,
          modelId: model.id,
          quantization: activeQuant,
          runtimes: multiRuntimes,
          profile,
          useLiveOllama,
          onProgress: (done, total, latest) => {
            collected.push(latest);
            setProgress({ done, total, latest });
            setRunStats([...collected]);
          },
        });
      } else {
        await runBenchmarkProfile({
          machineId: machine.id,
          modelId: model.id,
          quantization: activeQuant,
          runtime,
          profile,
          useLiveOllama: useLiveOllama && runtime === 'ollama',
          customPrompts,
          onProgress: (done, total, latest) => {
            collected.push(latest);
            setProgress({ done, total, latest });
            setRunStats([...collected]);
          },
        });
      }
    } finally {
      setRunning(false);
    }
  }

  function toggleMultiRuntime(r: RuntimeId) {
    setMultiRuntimes((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  const progressPct = progress.total === 0 ? 0 : (progress.done / progress.total) * 100;
  const avgTps =
    runStats.length === 0 ? 0 : runStats.reduce((s, r) => s + r.tokensPerSecond, 0) / runStats.length;
  const avgQuality =
    runStats.length === 0 ? 0 : runStats.reduce((s, r) => s + r.qualityScore, 0) / runStats.length;

  return (
    <div className="lab-bench">
      <section className="lab-bench-config">
        <h4>Configure a benchmark</h4>
        <div className="lab-bench-mode-toggle">
          <button
            type="button"
            className={`lab-mode-chip ${mode2 === 'single' ? 'lab-mode-active' : ''}`}
            onClick={() => setMode2('single')}
          >
            Single runtime
          </button>
          <button
            type="button"
            className={`lab-mode-chip ${mode2 === 'multi-runtime' ? 'lab-mode-active' : ''}`}
            onClick={() => setMode2('multi-runtime')}
          >
            Compare runtimes
          </button>
        </div>
        <label className="lab-bench-row">
          <span>Model</span>
          <select
            className="lab-select"
            value={modelId}
            onChange={(e) => {
              setModelId(e.target.value);
              const m = findModel(e.target.value);
              if (m) setQuant(recommendQuantForMachine(machine, m));
            }}
          >
            {MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="lab-bench-row">
          <span>Quant</span>
          <select
            className="lab-select"
            value={activeQuant}
            onChange={(e) => setQuant(e.target.value as Quantization)}
          >
            {model.quantizations.map((q) => (
              <option key={q} value={q}>
                {QUANT_LABEL[q]}
              </option>
            ))}
          </select>
        </label>
        {mode2 === 'single' ? (
          <label className="lab-bench-row">
            <span>Runtime</span>
            <select
              className="lab-select"
              value={runtime}
              onChange={(e) => setRuntime(e.target.value as RuntimeId)}
            >
              <option value="ollama">Ollama</option>
              <option value="lm-studio">LM Studio</option>
              <option value="llama-cpp">llama.cpp</option>
              <option value="mlx">MLX</option>
              <option value="vllm">vLLM</option>
            </select>
          </label>
        ) : (
          <div className="lab-bench-row">
            <span>Runtimes to compare</span>
            <div className="lab-runtime-checks">
              {ALL_RUNTIMES.map((r) => (
                <label key={r} className="lab-runtime-check">
                  <input
                    type="checkbox"
                    checked={multiRuntimes.includes(r)}
                    onChange={() => toggleMultiRuntime(r)}
                  />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <label className="lab-bench-row">
          <span>Profile</span>
          <select className="lab-select" value={profileId} onChange={(e) => setProfileId(e.target.value)}>
            {BENCHMARK_PROFILES.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <p className="lab-bench-help">{BENCHMARK_PROFILES.find((p) => p.id === profileId)?.description}</p>
        <label className="lab-bench-live-toggle">
          <input
            type="checkbox"
            checked={useLiveOllama}
            onChange={(e) => setUseLiveOllama(e.target.checked)}
            disabled={mode2 === 'single' && runtime !== 'ollama'}
          />
          <span>
            Use real Ollama inference{' '}
            {mode2 === 'single' && runtime !== 'ollama' && '(switch runtime to Ollama to enable)'}
          </span>
        </label>
        {mode === 'pro' && (
          <details
            className="lab-custom-prompts"
            open={customPromptsOpen}
            onToggle={(e) => setCustomPromptsOpen((e.target as HTMLDetailsElement).open)}
          >
            <summary>Custom prompt pack</summary>
            <p className="lab-bench-help">Override prompts per task. Empty = use built-in.</p>
            {(['coding', 'review', 'debugging', 'reasoning', 'docs', 'tool-use'] as TaskCategory[]).map(
              (t) => (
                <label key={t} className="lab-custom-prompt-row">
                  <span>{t}</span>
                  <textarea
                    className="lab-textarea"
                    rows={2}
                    value={customPrompts[t] ?? ''}
                    placeholder="(built-in)"
                    onChange={(e) => setCustomPrompts((prev) => ({ ...prev, [t]: e.target.value }))}
                  />
                </label>
              ),
            )}
          </details>
        )}
        <button type="button" className="primary-button" onClick={handleStart} disabled={running}>
          {running ? 'Benchmarking…' : 'Start benchmark'}
        </button>
        {mode === 'beginner' && !running && (
          <p className="lab-bench-beginner">
            Benchmarks run coding prompts against the selected model and record speed, quality, and stability.
            Every result gets a signed receipt you can share.
          </p>
        )}
      </section>

      <section className="lab-bench-progress">
        <header>
          <h4>Live progress</h4>
          <span>
            {progress.done}/{progress.total || estimatedTotalTasks(profileId)} tasks
          </span>
        </header>
        <Bar value={progressPct} max={100} tone="mint" />

        <div className="lab-bench-live-stats">
          <article>
            <span>Average tokens/sec</span>
            <strong>{avgTps.toFixed(1)}</strong>
          </article>
          <article>
            <span>Quality score</span>
            <strong>{avgQuality.toFixed(1)}</strong>
          </article>
          <article>
            <span>Latest task</span>
            <strong>{progress.latest?.taskCategory ?? '—'}</strong>
          </article>
        </div>

        <div className="lab-bench-stream">
          {runStats.length === 0 && <p className="lab-empty">Start a benchmark to see live tasks here.</p>}
          {runStats
            .slice()
            .reverse()
            .map((r) => (
              <div key={r.id} className="lab-bench-row-live">
                <span className="lab-bench-task">{r.taskCategory}</span>
                <span className="lab-bench-tps">{r.tokensPerSecond} tok/s</span>
                <span className="lab-bench-quality">Q {r.qualityScore.toFixed(1)}</span>
                <span className="lab-bench-ttft">{r.timeToFirstTokenMs}ms</span>
              </div>
            ))}
        </div>
      </section>

      <section className="lab-bench-history">
        <h4>History on {machine.nickname}</h4>
        {history.length === 0 ? (
          <p className="lab-empty">No runs yet on this machine.</p>
        ) : (
          <table className="lab-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Quant</th>
                <th>Runtime</th>
                <th>Task</th>
                <th>tok/s</th>
                <th>Quality</th>
                <th>Stability</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 40).map((r) => {
                const m = findModel(r.modelId);
                return (
                  <tr key={r.id}>
                    <td>{m?.displayName ?? r.modelId}</td>
                    <td>{QUANT_LABEL[r.quantization]}</td>
                    <td>{r.runtime}</td>
                    <td>{r.taskCategory}</td>
                    <td>{r.tokensPerSecond}</td>
                    <td>{r.qualityScore.toFixed(1)}</td>
                    <td>{(r.stabilityScore * 100).toFixed(0)}%</td>
                    <td>
                      <VerdictPill
                        verdict={
                          r.source === 'user-run' ? 'good' : r.source === 'community' ? 'usable' : 'usable'
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function estimatedTotalTasks(profileId: string): number {
  const profile = BENCHMARK_PROFILES.find((p) => p.id === profileId) ?? BENCHMARK_PROFILES[0];
  return profile.tasks.length * profile.iterations;
}
